import { bodyCopyBold, Col3Col9, ColFull, Modal, Pullout, PulloutType, TextArea } from '@aon/aon-tws-common-ui';
import React, { useReducer } from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../../contentConstants';
import { EditState, ITranslationEditItem } from '../../../../models';
import { TranslationHelper } from '../../../../utilities';
import { assertNonNullable } from '../../../../utilities/assertions';
import { useSurveyDefaultCultureCode } from '../../../../utilities/hooks';
import { TranslationEditor } from '../../../translations/TranslationEditor';

interface IQuestionTranslationsModalProps {
	isOpen: boolean;
	translations: ITranslationEditItem[];
	onCancel: () => void;
	onSubmit: (updatedTranslations: ITranslationEditItem[]) => void;
}

const StyledModal = styled(Modal)`
	.modal-body {
		flex-direction: column;
		gap: 1rem;
	}

	.tws-pullout-container {
		margin-top: 1rem;

		strong {
			${bodyCopyBold}
		}
	}

	.translation-warning {
		margin: auto;
		margin-bottom: 30px;
		width: 50%;

		.tws-pullout-container {
			margin-top: 0;
		}
	}
`;

interface IQuestionTranslationsModalState {
	defaultTranslation: ITranslationEditItem;
	nonDefaultTranslations: ITranslationEditItem[];
	hasDefaultTranslation: boolean;
	isDirty: boolean;
}

const translationIsWellDefined = (translation: ITranslationEditItem) => /\S+/.test(translation.textValue);

type DefaultTranslationChangedAction = {
	type: 'default-translation-changed';
	payload: { updatedTextValue: string; originalTranslation: ITranslationEditItem };
};

type NonDefaultTranslationChangedAction = {
	type: 'non-default-translation-changed';
	payload: {
		updatedTranslations: ITranslationEditItem[];
		originalTranslations: ITranslationEditItem[];
	};
};

type CancelAction = {
	type: 'cancel';
	payload: ITranslationEditItem[];
};

type SubmitAction = { type: 'submit' };

const reducer = (
	prevState: IQuestionTranslationsModalState,
	action: DefaultTranslationChangedAction | NonDefaultTranslationChangedAction | CancelAction | SubmitAction,
): IQuestionTranslationsModalState => {
	if (action.type === 'cancel') {
		return initializer({ translations: action.payload });
	}

	const newState = { ...prevState };

	switch (action.type) {
		case 'default-translation-changed': {
			const { updatedTextValue, originalTranslation } = action.payload;
			newState.defaultTranslation.textValue = updatedTextValue;
			newState.defaultTranslation.editState = TranslationHelper.getEditState(newState.defaultTranslation, originalTranslation);
			newState.hasDefaultTranslation = translationIsWellDefined(newState.defaultTranslation);
			break;
		}
		case 'non-default-translation-changed': {
			const { updatedTranslations, originalTranslations } = action.payload;
			newState.nonDefaultTranslations = TranslationHelper.updateTranslationEditState(updatedTranslations, originalTranslations);
			break;
		}
		case 'submit':
			newState.defaultTranslation.editState = EditState.None;
			newState.nonDefaultTranslations.forEach((t) => (t.editState = EditState.None));
			break;
	}

	newState.isDirty =
		newState.defaultTranslation.editState !== EditState.None || newState.nonDefaultTranslations.some((t) => t.editState !== EditState.None);
	return newState;
};

const initializer = (payload: { translations: ITranslationEditItem[]; defaultCultureCode?: string }): IQuestionTranslationsModalState => {
	const { defaultTranslation, nonDefaultTranslations } = payload.translations.reduce(
		(
			result: { defaultTranslation?: ITranslationEditItem; nonDefaultTranslations: ITranslationEditItem[] },
			value: ITranslationEditItem,
		) => {
			const newValue = { ...value, editState: EditState.None };

			if (value.cultureCode === payload.defaultCultureCode) {
				result.defaultTranslation = newValue;
			} else {
				result.nonDefaultTranslations.push(newValue);
			}

			return result;
		},
		{ nonDefaultTranslations: [] },
	);

	assertNonNullable(defaultTranslation);

	return {
		defaultTranslation,
		nonDefaultTranslations,
		hasDefaultTranslation: translationIsWellDefined(defaultTranslation),
		isDirty: false,
	};
};

const useQuestionTranslationsModal = ({
	translations,
	defaultCultureCode,
	onCancel,
	onSubmit,
}: Pick<IQuestionTranslationsModalProps, 'translations' | 'onCancel' | 'onSubmit'> & { defaultCultureCode: string }) => {
	const [state, dispatch] = useReducer(reducer, { translations, defaultCultureCode }, initializer);

	const onSubmitHandler = () => {
		dispatch({ type: 'submit' });
		const updatedTranslations = state.nonDefaultTranslations.concat(state.defaultTranslation);
		onSubmit(updatedTranslations);
	};

	const onCancelHandler = () => {
		dispatch({ type: 'cancel', payload: translations });
		onCancel();
	};

	const onDefaultTranslationChangeHandler = (updatedTextValue: string) => {
		const originalTranslation = translations.find((t) => t.cultureCode === defaultCultureCode);
		assertNonNullable(originalTranslation);
		dispatch({
			type: 'default-translation-changed',
			payload: { updatedTextValue, originalTranslation },
		});
	};

	const onNonDefaultTranslationChangeHandler = (updatedTranslations: ITranslationEditItem[]) => {
		const originalTranslations = translations.filter((t) => t.cultureCode !== defaultCultureCode);
		dispatch({
			type: 'non-default-translation-changed',
			payload: { updatedTranslations, originalTranslations },
		});
	};

	return {
		state,
		onDefaultTranslationChangeHandler,
		onNonDefaultTranslationChangeHandler,
		onSubmitHandler,
		onCancelHandler,
	};
};

export const QuestionTranslationsModal = ({ isOpen, ...rest }: IQuestionTranslationsModalProps) => {
	const surveyDefaultCultureCode = useSurveyDefaultCultureCode();
	const { state, onDefaultTranslationChangeHandler, onNonDefaultTranslationChangeHandler, onCancelHandler, onSubmitHandler } =
		useQuestionTranslationsModal({ ...rest, defaultCultureCode: surveyDefaultCultureCode });

	return (
		<StyledModal
			isOpen={isOpen}
			hideFooter={false}
			hideHeader={false}
			contentfullwidth={true}
			title={ContentConstants.SUBSECTIONHEADERS_QUESTIONTRANSLATIONS}
			primaryButtonText={ContentConstants.BUTTONS_UPDATE}
			primaryButtonDisabled={!state.hasDefaultTranslation || !state.isDirty}
			onSubmit={onSubmitHandler}
			secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
			secondaryButtonDisabled={false}
			onSecondaryButtonClick={onCancelHandler}
			onClosed={onCancelHandler}
		>
			{state.nonDefaultTranslations.length > 0 && state.defaultTranslation.editState !== EditState.None && (
				<div className='translation-warning'>
					<Pullout type={PulloutType.Warning}>
						<strong>{`${ContentConstants.WARNING_TRANSLATIONS_AFFECTED_HEADING.toUpperCase()}: `}</strong>
						{ContentConstants.WARNING_TRANSLATIONS_AFFECTED_BODY.replace('{property}', 'text').replace('{model}', 'question')}
					</Pullout>
				</div>
			)}
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_QUESTIONTEXT}>
				<TextArea
					placeholder={ContentConstants.INPUT_PLACEHOLDER_ENTER_QUESTION_TEXT}
					value={state.defaultTranslation.textValue}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => onDefaultTranslationChangeHandler(event.target.value)}
					error={!state.hasDefaultTranslation}
					message={ContentConstants.VALIDATION_MESSAGE_REQUIRED_FORM_FIELD.replace('{label}', 'Text')}
					maxLength={1000}
					required
				/>
			</Col3Col9>
			{state.nonDefaultTranslations.length > 0 && (
				<ColFull>
					<TranslationEditor
						inputType={'textarea'}
						enableHtmlEditor={false}
						showErrors={true}
						hasDefaultTranslation={state.hasDefaultTranslation}
						property={'Question'}
						propertyDisplayName={'Question'}
						onChange={onNonDefaultTranslationChangeHandler}
						translations={state.nonDefaultTranslations}
						maxLength={1000}
					/>
				</ColFull>
			)}
		</StyledModal>
	);
};
